import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import Landing from '@/pages/Landing';
import { LoginPage, RegisterPage, AuthPage } from '@/pages/Auth';
import ForgotPassword from '@/pages/ForgotPassword';
import Team from '@/pages/Team';
import Dashboard from '@/pages/Dashboard';
import Ingredients from '@/pages/Ingredients';
import Recipes from '@/pages/Recipes';
import MenuEngineering from '@/pages/MenuEngineering';
import Waste from '@/pages/Waste';
import Stock from '@/pages/Stock';
import Reports from '@/pages/Reports';
import Alerts from '@/pages/Alerts';
import Subscription from '@/pages/Subscription';
import Settings from '@/pages/Settings';
import AuditLog from '@/pages/AuditLog';
import ImportExport from '@/pages/ImportExport';

function AuthCallback() {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const searchParams = window.location.search;
    
    // Try to get session_id from hash or query params
    let sessionId = null;
    
    if (hash && hash.includes('session_id=')) {
      const params = new URLSearchParams(hash.replace('#', ''));
      sessionId = params.get('session_id');
    } else if (searchParams && searchParams.includes('session_id=')) {
      const params = new URLSearchParams(searchParams);
      sessionId = params.get('session_id');
    }

    console.log('AuthCallback - URL:', window.location.href);
    console.log('AuthCallback - session_id:', sessionId);

    if (sessionId) {
      processGoogleSession(sessionId)
        .then((user) => {
          console.log('Google session processed successfully:', user);
          // Clear the URL hash/params before navigating
          window.history.replaceState({}, document.title, '/dashboard');
          navigate('/dashboard', { replace: true });
        })
        .catch((err) => {
          console.error('Google session error:', err);
          setError(err.message || 'Error al procesar la sesión de Google');
          setTimeout(() => navigate('/auth', { replace: true }), 2000);
        });
    } else {
      console.log('No session_id found, redirecting to auth');
      navigate('/auth', { replace: true });
    }
  }, [processGoogleSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center flex-col gap-4">
        <p className="text-red-500">{error}</p>
        <p className="text-gray-400">Redirigiendo...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center flex-col gap-4">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400">Procesando inicio de sesión...</p>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  return <DashboardLayout>{children}</DashboardLayout>;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment or query params for session_id synchronously during render
  const hasSessionId = location.hash?.includes('session_id=') || 
                       location.search?.includes('session_id=');
  
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/ingredients" element={<ProtectedRoute><Ingredients /></ProtectedRoute>} />
      <Route path="/recipes" element={<ProtectedRoute><Recipes /></ProtectedRoute>} />
      <Route path="/menu-engineering" element={<ProtectedRoute><MenuEngineering /></ProtectedRoute>} />
      <Route path="/waste" element={<ProtectedRoute><Waste /></ProtectedRoute>} />
      <Route path="/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
      <Route path="/import-export" element={<ProtectedRoute><ImportExport /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
